import $api from '../api/index';
import { camelCase, pascalCase } from '../helpers';

// Store is only used for initialData because the api does not
// have access to the store without it being circular.

let loadPromise = null;

export const state = () => ({
  ready: false
});

export const mutations = {
  setReady(state, value) {
    state.ready = value;
  }
};

export const actions = {
  /**
   * @param {object} [options]
   * @param {boolean} [options.force] - Refetch even when initial data was already loaded (e.g. after login).
   */
  async getInitialData({ state, commit, dispatch }, options = {}) {
    const force = options?.force === true;

    if (state.ready && !force) return;

    if (loadPromise) {
      await loadPromise;
      if (state.ready && !force) return;
    }

    loadPromise = $api.initialData.getInitialData();
    let data;
    try {
      const result = await loadPromise;
      data = result.data;
    } finally {
      loadPromise = null;
    }

    dispatch(
      'auth/setValidated',
      { validated: data.validated === true },
      { root: true }
    );
    // Each pass is based off a single offer,
    // set the isPreview/type flag there
    dispatch(
      'offer/setOffer',
      {
        // Preview mode may not have an offer, but we need to set the
        // preview fields still
        ...(data?.offer || {}),
        isPreview: data.isPreview,
        isUnregisteredLink: data.isUnregisteredLink,
        previewDealId: data?.preview_deal_id,
        previewType: data?.preview_type,
        previewTypeId: data?.preview_type_id
      },
      { root: true }
    );

    ['deals', 'products', 'order_deals', 'order_products'].forEach(type => {
      if (data?.[type]?.length) {
        dispatch(`${camelCase(type)}/add${pascalCase(type)}`, data[type], {
          root: true
        });
      }
    });

    dispatch('progressTracker/initializeProgressTracker', null, { root: true });

    if (data.messageLists) {
      dispatch('unregisteredData/setMessageLists', data.messageLists, {
        root: true
      });
    }

    if (data.offerFields) {
      dispatch('unregisteredData/setOfferFields', data.offerFields, {
        root: true
      });
    }

    if (data.partner) {
      dispatch('partner/setPartner', data.partner, { root: true });
    }

    if (data.partner_user) {
      dispatch('partnerUser/setPartnerUser', data.partner_user, {
        root: true
      });
    }

    dispatch('favorites/setFavorites', data.favorites || {}, { root: true });

    // Daily Giveaway (ENG-2205): hold the section; the giveaway deal's page
    // activates it (giveaway/activateLive) when its route is opened.
    if (data.giveaway) {
      dispatch('giveaway/setPayload', data.giveaway, { root: true });
    }

    if (data.messageCounts) {
      commit('messages/setUnreadCount', parseInt(data.messageCounts.unread, 10), {
        root: true
      });
    }

    if (data.current_points) {
      commit('points/setPoints', data, { root: true });
    }

    // Preview data has a single venue, add any others here
    if (data.venue) {
      dispatch('venues/addVenues', [data.venue], { root: true });
    }

    commit('setReady', true);
  }
};

export default {
  namespaced: true,
  state,
  actions,
  mutations
};
